import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { SalesForceChatProvider } from 'Douleutaras/providers/chat/SalesForceChat.provider';
import { getDefaultLanguageCodeByCountryCode, useLocalization } from 'Douleutaras/hooks/localization';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { CaptureException } from 'Douleutaras/providers/CaptureException';

let SINGLETON_INSTANCE: SalesForceChatProvider | (() => SalesForceChatProvider);
// Shared so consumers mounted before the import resolves still receive the instance
let LOADING: Promise<SalesForceChatProvider> | undefined;

/**
 * This hook will create a new SalesForceChatProvider provider instance.
 */
export const useSalesForceChat = (): SalesForceChatProvider => {
  const { siteId } = useLocalization();
  const defaultLang = getDefaultLanguageCodeByCountryCode();
  const { t } = useTranslation();
  const user = useAppSelector(selectCurrentUser);
  const ff = useAppSelector(state => state.featureFlags);
  // tslint:disable-next-line:no-null-keyword
  const [provider, setProvider] = useState<SalesForceChatProvider | undefined>(SINGLETON_INSTANCE);

  useEffect(() => {
    if (Boolean(provider) || !ff.loaded || ff.entities?.useSalesforceChat === false) {
      return;
    }

    if (!LOADING) {
      const P = ff.entities?.useSalesforceChatNew ? 'SalesForceChatV2' : 'SalesForceChat';

      LOADING = import(`Douleutaras/providers/chat/${P}.provider`)
        .then(all => {
          const instance = all[`${P}Provider`].getInstance(t, defaultLang, siteId, user);
          SINGLETON_INSTANCE = instance;

          return instance;
        })
        .catch((error: Error) => {
          // Reset so the next consumer to mount retries, e.g. after a chunk-load failure.
          LOADING = undefined;
          CaptureException(error);

          return undefined;
        });
    }

    let isMounted = true;

    void LOADING.then(instance => {
      if (isMounted && instance) {
        setProvider(instance);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [ff.loaded]);

  return provider;
};
